import { Controller } from "@hotwired/stimulus";

// Vidéo de présentation de la landing.
//
// Elle démarre seule, en sourdine, quand elle entre dans le champ de vision —
// et un bouton permet de rétablir le son. Le fichier pèse ~35 Mo : il n'est
// donc jamais demandé tant que la vidéo n'est pas visible, et pas du tout
// quand la personne a activé l'économiseur de données ou navigue en 2G, où
// l'ancien comportement au clic reprend la main.
export default class extends Controller {
  static targets = ["poster", "sound", "soundOn", "soundOff", "soundLabel"];
  static values = { src: String };

  connect() {
    this.video = null;
    this.pausedByScroll = false;

    // Turbo fige le snapshot AVANT d'appeler disconnect() : sans ce démontage
    // anticipé, la page serait mise en cache avec la vidéo déjà injectée, et
    // le retour en arrière afficherait deux <video> superposées.
    this.beforeCacheHandler = () => this.#teardown();
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler);

    if (this.#dataSaverEnabled()) return;

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.#handleVisibility(entry)),
      { threshold: 0.45 },
    );
    this.observer.observe(this.element);
  }

  disconnect() {
    this.observer?.disconnect();
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler);
    this.#teardown();
  }

  // Turbo remplace le <body> à chaque navigation (changement de langue
  // notamment). Un <video> simplement détaché du DOM continue de jouer sa
  // bande-son dans Chrome : sans cet arrêt explicite, la voix de la page
  // précédente se superpose à la nouvelle vidéo, qui démarre en sourdine.
  #teardown() {
    if (!this.video) return;

    this.video.pause();
    // Couper la source puis recharger interrompt aussi le téléchargement en
    // cours : inutile de tirer 35 Mo pour une page qu'on vient de quitter.
    this.video.removeAttribute("src");
    this.video.load();
    this.video.remove();
    this.video = null;

    this.pausedByScroll = false;
    if (this.hasPosterTarget) this.posterTarget.hidden = false;
    if (this.hasSoundTarget) this.soundTarget.hidden = true;
  }

  // Lecture demandée explicitement : le son est rétabli d'emblée, puisque
  // c'est un geste de la personne.
  play() {
    this.#start({ muted: false });
  }

  toggleSound() {
    if (!this.video) return;

    this.video.muted = !this.video.muted;
    if (!this.video.muted) this.video.controls = true;
    this.#renderSoundButton();
  }

  #handleVisibility(entry) {
    if (entry.isIntersecting) {
      if (!this.video) {
        this.#start({ muted: true });
      } else if (this.pausedByScroll) {
        this.pausedByScroll = false;
        this.video.play().catch(() => {});
      }
      return;
    }

    // Sortie de l'écran : on suspend, sauf si la personne avait déjà mis en pause.
    if (this.video && !this.video.paused) {
      this.pausedByScroll = true;
      this.video.pause();
    }
  }

  #start({ muted }) {
    if (this.video) return;

    const video = document.createElement("video");
    video.src = this.srcValue;
    video.muted = muted;
    video.autoplay = true;
    video.playsInline = true;
    video.preload = "auto";
    video.controls = !muted;
    video.className = "block w-full";
    video.addEventListener("ended", () => this.#renderSoundButton());

    this.video = video;
    // Le poster est masqué, pas remplacé : Turbo met la page en cache telle
    // qu'elle est à l'instant de la navigation, et un poster détruit ne
    // reviendrait jamais au retour en arrière — la vidéo recréée n'aurait
    // alors plus rien où s'insérer.
    if (this.hasPosterTarget) {
      this.posterTarget.insertAdjacentElement("afterend", video);
      this.posterTarget.hidden = true;
    }

    video.play().catch(() => {
      // Lecture automatique refusée par le navigateur : on rend la main
      // aux contrôles natifs plutôt que de laisser un écran figé.
      video.controls = true;
    });

    if (this.hasSoundTarget) this.soundTarget.hidden = false;
    this.#renderSoundButton();
  }

  #renderSoundButton() {
    if (!this.hasSoundTarget || !this.video) return;

    const muted = this.video.muted;
    if (this.hasSoundOnTarget) this.soundOnTarget.hidden = muted;
    if (this.hasSoundOffTarget) this.soundOffTarget.hidden = !muted;

    const label = muted
      ? this.soundTarget.dataset.labelUnmute
      : this.soundTarget.dataset.labelMute;
    if (label) {
      this.soundTarget.setAttribute("aria-label", label);
      if (this.hasSoundLabelTarget) this.soundLabelTarget.textContent = label;
    }
    this.soundTarget.setAttribute("aria-pressed", String(!muted));
  }

  // Économiseur de données ou réseau lent : on ne télécharge rien d'office.
  #dataSaverEnabled() {
    const connection = navigator.connection;
    if (!connection) return false;

    return Boolean(connection.saveData) || /(^|-)2g$/.test(connection.effectiveType || "");
  }
}
